import { action, computed, observable } from 'mobx'
import { type IValidationSchema, required, validationContext } from 'validx'
import {
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { InventoryItemQuestion } from '@/features/legends/questions/questions'

/**
 * Adds a service item to the job, with a quantity effects can change.
 */
export class InventoryItemQuestionEditorViewModel extends QuestionEditorViewModelBase<InventoryItemQuestion> {
  private readonly _schema: IValidationSchema<InventoryItemQuestionEditorViewModel> =
    {
      ...QuestionEditorViewModelBase.sharedSchema,
      inventoryItemId: [required('The service item ID is required.')],
      quantity: [required('Quantity required')],
    }

  readonly type = 'InventoryItem'

  @observable accessor label = ''

  /**
   * The service item's ID.
   */
  @observable accessor inventoryItemId = ''
  /**
   * `null` while the amount field is empty, which fails validation instead of
   * saving 0.
   */
  @observable accessor quantity: number | null = 0

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options)
    this._validateWhileEditing(this._schema)
  }

  /** @inheritDoc */
  get displayText() {
    return this.label
  }

  /**
   * The picked service item.
   */
  @computed
  get inventoryItem() {
    return (
      this.dependencies.catalog.allowedInventoryItems.find(
        (i) => i.id === this.inventoryItemId,
      ) ?? null
    )
  }

  /**
   * Service items that aren't in the legend yet, plus this element's own.
   */
  @computed
  get inventoryItemElementOptions() {
    const alreadyAdded = this.parentQuestionsBuilder.allQuestions.flatMap(
      (q) => (q.type === 'InventoryItem' ? [q.inventoryItemId] : []),
    )
    return this.dependencies.catalog.allowedInventoryItems
      .flatMap((item) =>
        item.id &&
        (!alreadyAdded.includes(item.id) || item.id === this.inventoryItemId)
          ? [{ id: item.id, text: `${item.name} - ${item.shortId}` }]
          : [],
      )
      .sort((a, b) => a.text.localeCompare(b.text))
  }

  /**
   * Sets the amount.
   */
  @action.bound
  setQuantity(quantity: number | null) {
    this.quantity = quantity
  }

  /**
   * Picks the service item, deriving the element's label from it, and its ID
   * unless another element has that ID already.
   */
  @action.bound
  changeInventoryItem(inventoryItemId: string) {
    const item = this.dependencies.catalog.allowedInventoryItems.find(
      (i) => i.id === inventoryItemId,
    )
    if (!item) {
      return
    }
    const id =
      'ITEM_' +
      item.name
        .trim()
        .replaceAll('-', '__')
        .replace(/[^a-zA-Z0-9_ ]/g, '')
        .replaceAll(' ', '_')
        .toUpperCase()
    if (QuestionEditorViewModelBase.idValidation.trySetId(this, id)) {
      this.idDraft = id
    }
    this.label = item.name
    this.inventoryItemId = inventoryItemId
  }

  /** @inheritDoc */
  @action
  hydrate(question: InventoryItemQuestion) {
    this._hydrateShared(question)
    this.label = question.label ?? ''
    this.inventoryItemId = question.inventoryItemId ?? ''
    this.quantity = question.quantity === undefined ? 0 : question.quantity
  }

  /** @inheritDoc */
  protected _serialize(): InventoryItemQuestion {
    return {
      ...this._serializeShared(),
      type: this.type,
      label: this.label,
      inventoryItemId: this.inventoryItemId,
      quantity: this.quantity,
    }
  }
}
